import React from 'react';
import {
  type ChoiceFieldProps,
  useDroppedRowsWarning,
} from '@ns/plugin-picker-shared';
import type { UseQueryResult } from '@tanstack/react-query';

import type { DirectoryOptions } from './directory-options';
import { type DirectoryPagePreview, directoryQueryState } from './query-state';

const DIRECTORY_SEARCH_DEBOUNCE_MS = 200;

/**
 * What differs between the group and the service-principal picker.
 *
 * @typeParam TOption - The option the picker offers.
 */
export interface DirectoryPickerSettings<TOption> {
  /** The label of the dropped-rows warning, such as `Entra groups`. */
  readonly droppedRowsLabel: string;
  /** The plural in the empty text, such as `groups`. */
  readonly noun: string;
  /** Runs the search for one settled term. Called on every render, so it is a hook. */
  readonly useSearch: (
    term: string,
  ) => UseQueryResult<
    DirectoryOptions<TOption> & Pick<DirectoryPagePreview, 'more'>,
    Error
  >;
}

/**
 * The `ChoiceField` props a directory picker takes from {@link useDirectoryPicker}.
 *
 * @typeParam TOption - The option the picker offers.
 */
export type DirectoryPicker<TOption> = Required<
  Pick<ChoiceFieldProps<TOption>, 'emptyText' | 'remoteSearch' | 'state'>
> &
  Pick<ChoiceFieldProps<TOption>, 'items'>;

/**
 * Everything a directory picker passes to `ChoiceField` besides the value, its labels and its source key.
 *
 * @remarks
 * The raw combobox text stays local until the 200 ms debounce settles. Only
 * that settled term reaches the search, and so the query key and Graph.
 *
 * @typeParam TOption - The option the picker offers.
 * @param settings - What differs per picker.
 * @returns `emptyText`, `items`, `remoteSearch` and `state` for `ChoiceField`.
 */
export function useDirectoryPicker<TOption>(
  settings: DirectoryPickerSettings<TOption>,
): DirectoryPicker<TOption> {
  const [rawTerm, setRawTerm] = React.useState('');
  const [settledTerm, setSettledTerm] = React.useState('');

  React.useEffect(() => {
    const debounceTimer = setTimeout(
      () => setSettledTerm(rawTerm),
      DIRECTORY_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(debounceTimer);
  }, [rawTerm]);

  const query = settings.useSearch(settledTerm);
  // Placeholder data is the previous page, already logged when it arrived.
  useDroppedRowsWarning(
    settings.droppedRowsLabel,
    query.isPlaceholderData ? undefined : query.data?.droppedRowCount,
    query.dataUpdatedAt,
  );
  const presentation = directoryQueryState({
    error: query.error,
    isError: query.isError,
    isPending: query.isFetching,
    page: query.data,
    refetch: () => void query.refetch(),
  });
  return {
    emptyText:
      rawTerm === ''
        ? `No ${settings.noun} found`
        : `No ${settings.noun} found for "${rawTerm}"`,
    items: query.data?.options,
    remoteSearch: {
      mayDisproveSelection:
        settledTerm === '' &&
        presentation.choiceState.kind === 'ready' &&
        presentation.isComplete,
      onInputValueChange: setRawTerm,
    },
    state: presentation.choiceState,
  };
}
